import {injectable} from "inversify";
import Scene = Phaser.Scene;
import ExperiencePanel from "@/scenes.ui/experience-panel";
import {LevelUpPanel} from "@/scenes.ui/level-up-panel";
import {LevelUpDto} from "@/store/level/types";
import {Listener} from "@/store/listener";
import ListenerManager from "@/managers/listener-manager";
import {InterfaceMode, Player} from "@/store/player/types";
import {ExperienceInfoPanel} from "@/scenes.ui/experience-info-panel";
import LevelStore from "@/store/level";
import PlayerStore from "@/store/player";
import SoundUtils from "@/utils/sound-utils";

@injectable()
export class ExperienceService implements Listener{

    private readonly LEVEL_UP_PANEL_DELAY: number = 400;

    private experiencePanel!: ExperiencePanel;

    private levelUpPanel!: LevelUpPanel | undefined;

    private scene!: Scene;

    private canvas!: HTMLCanvasElement;

    private mouseEnabledCallback: ((enabled: boolean) => unknown) | undefined;

    private awardSound!: Phaser.Sound.BaseSound | undefined;
    private creatureClickSound!: Phaser.Sound.BaseSound | undefined;

    public setSceneAndCanvas(scene: Scene, canvas: HTMLCanvasElement, awardSound: Phaser.Sound.BaseSound | undefined,
                             creatureClickSound: Phaser.Sound.BaseSound | undefined, mouseEnabledCallback: (enabled: boolean) => unknown,
                             interfaceMode: InterfaceMode | undefined = undefined) {
        this.scene = scene;
        this.canvas = canvas;
        this.mouseEnabledCallback = mouseEnabledCallback;
        this.awardSound = awardSound;
        this.creatureClickSound = creatureClickSound;
        if (this.levelUpPanel) {
            this.levelUpPanel.close();

            this.levelUpPanel = undefined;
        }

        this.putExperienceBarsOnScene(interfaceMode);

        ListenerManager.addPlayerListener(this);
        this.onChange();
    }

    public stopListenPlayer() {
        ListenerManager.removePlayerListener(this);
    }

    public changeChildrenPosition(y: number) {
        this.experiencePanel.changeChildrenPosition(y);
    }

    private putExperienceBarsOnScene(interfaceMode: InterfaceMode | undefined): ExperiencePanel {

        let player: Player = PlayerStore.getPlayer()!;
        let maxExp = player.maxExp;
        let currentExp = player.currentExp;
        let level = player.level;

        this.experiencePanel = new ExperiencePanel(this.scene, this.canvas, currentExp, maxExp,
            ExperienceService.findOutCurrentBarWidth(currentExp, maxExp), level,
            () => {
                player = PlayerStore.getPlayer()!;
                this.showExpInfoWindow(this.scene, this.canvas, player.currentExp, player.maxExp, player.level);
            }, interfaceMode);

        this.scene.add.existing(this.experiencePanel);

        return this.experiencePanel;
    }

    private showExpInfoWindow(scene: Scene, canvas: HTMLCanvasElement, currentExp: number, maxExp: number, level: number) {
        LevelStore.fetchLevelUpDto().then(dto => {
            new ExperienceInfoPanel(scene, canvas, currentExp, maxExp, level, dto.skillDelta).open();
        })

    }

    private async showLevelUpWindow() {

        if (!this.levelUpPanel) {
            let levelUpDto: LevelUpDto = await LevelStore.fetchLevelUpDto();

            this.scene.time.delayedCall(this.LEVEL_UP_PANEL_DELAY, () =>{

                if (!this.levelUpPanel && this.scene.scene.isActive()) {
                    SoundUtils.play(this.awardSound);

                    this.levelUpPanel = new LevelUpPanel(this.scene, this.canvas,
                        levelUpDto.upgradeCreaturesIdList, PlayerStore.getPlayer()!.level, levelUpDto,
                        (creatureId: number) => this.levelUpCreature(creatureId));

                    this.levelUpPanel.openAnimated();

                    if (this.mouseEnabledCallback) {
                        this.mouseEnabledCallback!(false);
                    }
                }
            });
        }
    }

    private static timeToLevelUp() {
        return PlayerStore.getPlayer()!.currentExp >= PlayerStore.getPlayer()!.maxExp;
    }

    private async levelUpCreature(creatureId: number) {

        SoundUtils.play(this.creatureClickSound);

        LevelStore.levelUpChosenCreature(creatureId);

        if (this.levelUpPanel) {
            this.levelUpPanel.close();
            this.levelUpPanel = undefined;
        }

        if (this.mouseEnabledCallback) {
            this.mouseEnabledCallback!(true);
        }
        // if (this.mouseEnabledCallback && BasePanel.isAllClosed()) {
        //     this.mouseEnabledCallback!(true);
        // } else {
        //     console.log('NOT All Closed');
        // }
    }

    private updateExpPanel() {
        let player: Player = PlayerStore.getPlayer();
        this.experiencePanel.updateValuesText(player.currentExp, player.maxExp, player.level);
        this.experiencePanel.setExpFillerLine(ExperienceService.findOutCurrentBarWidth(player.currentExp, player.maxExp));
    }

    private static findOutCurrentBarWidth(currentExp: number, maxExp: number): number {
        let percentOfCurrentExp = currentExp / maxExp;
        return percentOfCurrentExp >= 1 ? 1 : percentOfCurrentExp;
    }

    onChange(): void {
        if (ExperienceService.timeToLevelUp()) {
            this.showLevelUpWindow();
        }
        this.updateExpPanel();
    }

}
